import React, { useState, useRef, useEffect } from 'react';
import './BlogSection.css';

const BlogSection = () => {
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(5000); // 충분히 큰 높이로 설정
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const timeoutRef = useRef(null);
  
  const BLOG_URL = 'https://blog.flowto-ai.com/';
  const MAX_RETRY_COUNT = 3;
  const LOAD_TIMEOUT = 10000; // 10초

  // postMessage 보안 강화: Origin 검증
  useEffect(() => {
    const handleMessage = (event) => {
      // Origin 검증: 허용된 origin만 메시지 수신
      const allowedOrigins = [
        'https://blog.flowto-ai.com',
        'http://localhost:3000',
        'http://localhost:3001'
      ];
      
      if (!allowedOrigins.includes(event.origin)) {
        console.warn('차단된 origin에서 메시지 수신:', event.origin);
        return;
      }

      // iframe에서 보낸 높이 정보를 받아서 설정
      if (event.data && typeof event.data === 'object' && event.data.type === 'iframe-height') {
        setIframeHeight(event.data.height);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // iframe 로드 및 타임아웃 관리
  useEffect(() => {
    if (hasError) return;

    // 타임아웃 설정
    timeoutRef.current = setTimeout(() => {
      if (isLoading) {
        console.error('블로그 로딩 타임아웃');
        setHasError(true);
        setIsLoading(false);
      }
    }, LOAD_TIMEOUT);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoading, hasError]);

  // 블로그 URL 접근 가능 여부 사전 확인
  useEffect(() => {
    const checkBlogAvailability = async () => {
      try {
        // HEAD 요청으로 블로그 사이트 접근 가능 여부 확인
        const response = await fetch(BLOG_URL, {
          method: 'HEAD',
          mode: 'no-cors', // CORS 우회 (응답은 읽을 수 없지만 요청은 가능)
          cache: 'no-cache'
        });
        // no-cors 모드에서는 응답을 읽을 수 없지만, 요청 자체가 실패하면 catch로 이동
      } catch (error) {
        console.warn('블로그 사이트 접근 확인 중 오류:', error);
        // 에러가 발생해도 iframe 로드는 시도 (일부 환경에서는 HEAD 요청이 실패할 수 있음)
      }
    };

    checkBlogAvailability();
  }, []);

  const handleIframeLoad = () => {
    // 타임아웃 클리어
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // iframe이 로드되었지만 실제로 콘텐츠가 있는지 확인
    setTimeout(() => {
      try {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
          // iframe 내부에 접근 가능한지 확인
          try {
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            // 접근 가능하면 로드 성공
            setIsLoading(false);
            setHasError(false);
            
            // postMessage를 통해 높이를 요청
            iframe.contentWindow.postMessage({ type: 'request-height' }, BLOG_URL);
          } catch (e) {
            // Cross-origin 제한으로 접근 불가능한 경우
            // 이는 정상적인 경우일 수 있으므로 로드 성공으로 간주
            setIsLoading(false);
            setHasError(false);
          }
        } else {
          setIsLoading(false);
          setHasError(false);
        }
      } catch (error) {
        console.log('iframe 로드 확인 중 오류:', error);
        setIsLoading(false);
        setHasError(false);
      }
    }, 1000);
  };

  const handleIframeError = () => {
    // 타임아웃 클리어
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    console.error('블로그 iframe 로드 실패');
    setHasError(true);
    setIsLoading(false);
  };

  const handleRetry = () => {
    if (retryCount >= MAX_RETRY_COUNT) {
      return;
    }

    setRetryCount(prev => prev + 1);
    setHasError(false);
    setIsLoading(true);

    // iframe src 재설정하여 재시도
    if (iframeRef.current) {
      iframeRef.current.src = BLOG_URL + '?retry=' + Date.now();
    }
  };

  const handleOpenExternal = () => {
    window.open(BLOG_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="blog" className="blog-section">
      <div className="blog-container">
        <div className="blog-header">
          <h2 className="blog-title">Flowto.AI 블로그</h2>
          <p className="blog-subtitle">최신 AI 트렌드와 인사이트를 만나보세요</p>
        </div>
        
        <div className="blog-iframe-container">
          {/* 로딩 상태 */}
          {isLoading && !hasError && (
            <div className="blog-loading">
              <div className="blog-loading-spinner"></div>
              <p className="blog-loading-text">블로그를 불러오는 중...</p>
            </div>
          )}

          {/* 에러 상태 */}
          {hasError && (
            <div className="blog-error">
              <div className="blog-error-icon">⚠️</div>
              <h3 className="blog-error-title">블로그를 불러올 수 없습니다</h3>
              <p className="blog-error-message">
                블로그 사이트({BLOG_URL})에 일시적으로 접근할 수 없거나 다운되었을 수 있습니다.
                {retryCount >= MAX_RETRY_COUNT && ' 최대 재시도 횟수를 초과했습니다.'}
              </p>
              <p className="blog-error-suggestion">
                아래 버튼을 클릭하여 블로그를 새 탭에서 직접 열어보세요.
              </p>
              <div className="blog-error-actions">
                {retryCount < MAX_RETRY_COUNT && (
                  <button 
                    className="blog-retry-button" 
                    onClick={handleRetry}
                  >
                    다시 시도 ({retryCount + 1}/{MAX_RETRY_COUNT})
                  </button>
                )}
                <button 
                  className="blog-external-link-button" 
                  onClick={handleOpenExternal}
                >
                  블로그 새 탭에서 열기
                </button>
              </div>
            </div>
          )}

          {/* iframe */}
          <iframe
            ref={iframeRef}
            src={BLOG_URL}
            title="Flowto.AI Blog"
            className={`blog-iframe ${hasError ? 'blog-iframe-hidden' : ''}`}
            frameBorder="0"
            scrolling="no"
            style={{ height: `${iframeHeight}px` }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
